import type { RowData } from "@tanstack/react-table";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  tableColumnWidthSF,
  type IColumnDefMeta,
  type IRydeHeader,
} from "@ryde/shared";

import { isColumnResizable, resolveHeaderColumnWidth } from "../column-resize";

interface IUseGridTableHeadCellParams<T extends RowData> {
  header: IRydeHeader<T, unknown>;
  isTableSized: boolean;
  isColumnResizeEnabled: boolean;
}

export const useGridTableHeadCell = <T extends RowData>({
  header,
  isTableSized,
  isColumnResizeEnabled,
}: IUseGridTableHeadCellParams<T>) => {
  const { entity } = usePageContext();
  const columnId = header.column.id;
  const meta = header.column.columnDef.meta as IColumnDefMeta | undefined;
  const sizedWidth = useRecoilValue(tableColumnWidthSF([entity, columnId]));

  return {
    columnId,
    columnLabel: meta?.displayName,
    columnWidth: resolveHeaderColumnWidth({
      // A grid without resizing (e.g. a nested grid in a form) shares the page entity, so it ignores its widths.
      sizedWidth: isColumnResizeEnabled ? sizedWidth : undefined,
      metaWidth: meta?.columnWidth,
      isTableSized,
    }),
    isResizable:
      isColumnResizeEnabled && !header.isPlaceholder && isColumnResizable(meta),
  };
};
